import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import getConfig from 'getConfig';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as SavedSearchesActions from 'search/saved-searches/SavedSearchesActions';
import SavedSearchSettingsModal, {
  SavedSearchSettingsModalModifiableFields,
} from 'search/saved-searches/SavedSearchSettingsModal';
import {
  convertFromGraphqlSavedSearchParams,
  NonPaginatedSearchParams,
  SavedSearch,
} from 'search/saved-searches/SavedSearchUtil';
import {SearchParams} from 'search/SearchConstants';
import {BLUE_LIGHT, GRAY_DARK, RED_DARK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';

interface SavedSearchOptionsMenuProps {
  searchSection: SearchSection;
  onSaved: (savedSearch?: SavedSearch) => void;
  onDeleted: () => void;
  onSearch: (searchParams: SearchParams) => void;
  targetSavedSearch: SavedSearch;
  searchParams: SearchParams;
  hasUnsavedChanges: boolean;
  hasUpdate: boolean;
  hasReset: boolean;
  dropLeft?: boolean;
}

export function SavedSearchOptionsMenu({
  searchSection,
  onSaved,
  onDeleted,
  onSearch,
  targetSavedSearch,
  searchParams,
  hasUnsavedChanges,
  hasUpdate,
  hasReset,
  dropLeft,
}: SavedSearchOptionsMenuProps): JSX.Element {
  const updatedSearchParams: NonPaginatedSearchParams = {
    sorts: searchParams.sorts,
    filters: searchParams.filters,
    query: searchParams.query,
  };

  if (searchParams.groupBy) {
    updatedSearchParams.groupBy = searchParams.groupBy;
  }

  const onDelete = async (): Promise<boolean> => {
    const deleted = await SavedSearchesActions.confirmAndDelete(targetSavedSearch, searchSection);
    if (deleted) {
      onDeleted();
    }
    return deleted;
  };

  const onSaveExisting = async (values: SavedSearchSettingsModalModifiableFields): Promise<void> => {
    const updatedSavedSearch = await SavedSearchesActions.update(targetSavedSearch.id, {
      name: values.name,
      description: values.description,
      ownerJson: values.ownerJson,
      ownerInfoJson: values.ownerInfoJson,
      collaboratorsJson: {collaborators: values.collaboratorsJson},
    });
    onSaved(updatedSavedSearch);
  };

  return (
    <Dropdown
      menu={
        <Menu height={MenuHeight.MEDIUM} width={MenuWidth.MINI} dropLeft={dropLeft}>
          {compact([
            hasUpdate ? (
              <Option
                key="update"
                onSelect={async () => {
                  const updatedSavedSearch = await SavedSearchesActions.update(targetSavedSearch.id, {
                    searchParams: updatedSearchParams,
                  });
                  onSaved(updatedSavedSearch);
                }}
                value="Update"
                isDisabled={!(hasUnsavedChanges && targetSavedSearch.permissions.writable)}
              >
                Update
              </Option>
            ) : null,
            hasReset ? (
              <Option
                key="reset"
                onSelect={() => {
                  onSearch({
                    ...searchParams,
                    ...convertFromGraphqlSavedSearchParams(targetSavedSearch.searchParams),
                  });
                }}
                value="Reset"
                isDisabled={!hasUnsavedChanges}
              >
                Reset
              </Option>
            ) : null,
            getConfig('ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH') ? (
              <Option
                key="share"
                onSelect={async () => {
                  await runModal(SavedSearchSettingsModal, {
                    savedSearchId: targetSavedSearch.id,
                    title: 'Saved Search Settings',
                    onDelete: targetSavedSearch.permissions.admin ? onDelete : undefined,
                    onRequestSubmit: onSaveExisting,
                    initialValues: {
                      name: targetSavedSearch.name,
                      description: targetSavedSearch.description,
                    },
                    autoFocus: 'share',
                    noWriteAccess: !targetSavedSearch.permissions.writable,
                  });
                }}
                value="Share"
              >
                Share
              </Option>
            ) : null,
            <Option
              key="copy"
              onSelect={async () => {
                await runModal(SavedSearchSettingsModal, {
                  savedSearchId: targetSavedSearch.id,
                  title: 'Saved Search Settings',
                  initialValues: {
                    name: `Copy of ${targetSavedSearch.name}`,
                    description: targetSavedSearch.description,
                  },
                  onRequestSubmit: async (values: SavedSearchSettingsModalModifiableFields) => {
                    const savedSearch = await SavedSearchesActions.create({
                      name: values.name,
                      description: values.description,
                      section: targetSavedSearch.section,
                      params: updatedSearchParams,
                      ownerJson: values.ownerJson,
                      ownerInfoJson: values.ownerInfoJson,
                      collaboratorsJson: {collaborators: values.collaboratorsJson},
                    });
                    onSaved(savedSearch);
                  },
                  autoFocus: 'name',
                  copy: true,
                });
              }}
              value="Save a copy"
            >
              Save a copy
            </Option>,
            <Option
              key="rename"
              onSelect={async () => {
                await runModal(SavedSearchSettingsModal, {
                  savedSearchId: targetSavedSearch.id,
                  title: 'Saved Search Settings',
                  onDelete: targetSavedSearch.permissions.admin ? onDelete : undefined,
                  onRequestSubmit: onSaveExisting,
                  initialValues: {
                    name: targetSavedSearch.name,
                    description: targetSavedSearch.description,
                  },
                  autoFocus: 'name',
                });
              }}
              value="Rename"
              isDisabled={!targetSavedSearch.permissions.writable}
            >
              Rename
            </Option>,
            <Option
              key="delete"
              onSelect={async (): Promise<void> => {
                if (await SavedSearchesActions.confirmAndDelete(targetSavedSearch, searchSection)) {
                  onDeleted();
                }
              }}
              value="Delete"
              isDisabled={!targetSavedSearch.permissions.admin}
            >
              <div className={css(targetSavedSearch.permissions.admin ? styles.deleteText : null)}>
                Delete
              </div>
            </Option>,
          ])}
        </Menu>
      }
    >
      <BenchlingIcon className={classNames(css(styles.icon))} icon="options" />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  icon: {
    fontSize: '14px',
    color: GRAY_DARK,
    ':hover': {
      color: BLUE_LIGHT,
    },
    cursor: 'pointer',
    marginLeft: 6,
    whiteSpace: 'nowrap',
    padding: '10px',
  },
  deleteText: {
    color: RED_DARK,
  },
});
